import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { AlertComponent, Button } from "..";
import LexicalEditor from "../text-editor/text-editor";
import "./terms-of-service.scss";
import {
  getTermsOfService,
  saveTermsOfService,
} from "../../../api/terms-of-service";
import { useSetLoading } from "../../hooks/appearance";

interface TermsOfServiceScreenProps {
  viewOnly?: boolean;
  variant?: "admin" | "provider" | "preneed-planner" | "public";
}

const TermsOfServiceScreen: React.FC<TermsOfServiceScreenProps> = ({
  viewOnly = false,
  variant = "admin",
}) => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showAFPPolicy = false;
  const [editedPrivacyPolicy, setEditedPrivacyPolicy] = useState<{
    privacyPolicyJson: any;
    privacyPolicyHtml: any;
  }>({ privacyPolicyJson: null, privacyPolicyHtml: null });
  const [privacyPolicy, setPrivacyPolicy] = useState<any>(null);
  const [defaultPrivacyPolicy, setDefaultPrivacyPolicy] = useState<any>(null);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});

  const handleCancel = () => {
    setIsEdit(false);
    setEditedPrivacyPolicy({
      privacyPolicyJson: null,
      privacyPolicyHtml: null,
    });
  };

  const handleSaveTermsOfService = async (data: any) => {
    try {
      setIsLoading(true);
      await saveTermsOfService(data);
      fetchTermsOfService();
      setIsEdit(false);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching terms of service:", err);
      setAlert({
        isOpen: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          err?.error ||
          "Error fetching terms of service.",
        type: "error",
      });
      setIsLoading(false);
    }
  };

  const handleSave = async (html: any, children: any) => {
    const hasMeaningfulContent = (rawHtml: string): boolean => {
      const normalized = rawHtml
        .replace(/&nbsp;/gi, " ")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      return normalized.length > 0;
    };

    const data = {
      content: html,
      privacyPolicyJson: children,
      privacyPolicyHtml: html,
    };

    if (!hasMeaningfulContent(html || "")) {
      setAlert({
        isOpen: true,
        message: "Add your Terms of Service so users know what to expect.",
        type: "error",
      });
      return;
    }

    handleSaveTermsOfService(data);
  };

  const fetchTermsOfService = async () => {
    try {
      setIsLoading(true);
      const response = await getTermsOfService(variant);
      const tos = response?.data?.data?.data || response?.data?.data || null;
      setDefaultPrivacyPolicy(tos);
      setPrivacyPolicy(tos);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching terms of service:", err);
      setIsLoading(false);

      setAlert({
        isOpen: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          err?.error ||
          "Error fetching terms of service.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchTermsOfService();
  }, []);

  useEffect(() => {
    if (viewOnly) {
      setIsEdit(false);
    }
  }, [viewOnly]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const handleStartEdit = () => {
    if (viewOnly) return;
    setIsEdit(true);
  };

  const showReadOnly = viewOnly || !isEdit;

  return (
    <div
      className={`admin-terms-of-service ${variant !== "admin" ? `${variant}-terms-of-service` : ""
        }`}
    >
      <div className="terms-of-service-editor">
        <div className="terms-of-service-editor-card">
          {(variant === "admin" || variant === "public") && (
            <div className="terms-of-service-card-header">
              <h4>
                {variant === "public"
                  ? "Terms of Service"
                  : isEdit
                    ? "Edit Terms of Service"
                    : "AFP Terms of Service"}
              </h4>
              {!isEdit && !viewOnly && variant === "admin" && (
                <Button
                  label="Edit"
                  className="terms-of-service-edit-btn"
                  onClick={handleStartEdit}
                />
              )}
            </div>
          )}
          {!showReadOnly ? (
            <>
              <Box className={`editor-wrapper ${isEdit ? "editor-wrapper-edit" : ""}`}>
                <LexicalEditor
                  handleDataChange={(html: any, children: any) => {
                    setEditedPrivacyPolicy({
                      privacyPolicyJson: children,
                      privacyPolicyHtml: html,
                    });
                  }}
                  placeholderText="Enter your terms of service here..."
                  defaultHtmlJson={
                    showAFPPolicy
                      ? defaultPrivacyPolicy?.content
                      : privacyPolicy?.content
                  }
                />
              </Box>
              <Box className="terms-of-service-actions">
                <Button
                  label="Save"
                  className="terms-of-service-save-btn"
                  onClick={() => {
                    handleSave(
                      editedPrivacyPolicy?.privacyPolicyHtml,
                      editedPrivacyPolicy?.privacyPolicyJson
                    );
                  }}
                />
                <Button
                  label="Cancel"
                  className="terms-of-service-cancel-btn"
                  onClick={handleCancel}
                />
              </Box>
            </>
          ) : (
            <div className="terms-of-service-readonly">
              <div
                className="terms-of-service-readonly-content"
                dangerouslySetInnerHTML={{
                  __html:
                    privacyPolicy?.content ||
                    (viewOnly ? "" : "<p>Enter your terms of service here...</p>"),
                }}
              />
            </div>
          )}
        </div>
      </div>

      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};

export default TermsOfServiceScreen;
